// Printable Form WH-347 — Department of Labor Davis-Bacon Certified Payroll.
// Loads obligation data, populates a recognizable WH-347 layout, and lets
// the user Print → Save as PDF. Same pattern as ReimbursementPacketPage.
//
// Source: Form WH-347 (rev. revised periodically) per 29 CFR 5.5 / 40 USC 3141.
// This is a demo-grade approximation, NOT for production submission.

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

type ObligationDetail = {
  id: string;
  due_date: string;
  period_start: string | null;
  period_end: string | null;
  status: string;
  notes: string | null;
  code: string;
  name: string;
};

const DEMO_PROJECT_ID = '85a46d17-50f3-4bcc-be60-68074efcd5c7';

// Synthesized crew roster for the demo. Realistic CO Davis-Bacon wage
// determinations for fiber-related classifications.
const ROSTER: Array<{
  name: string; class: string;
  rate: number; fringe: number;
  hours: [number, number, number, number, number, number, number]; // M T W T F S S
}> = [
  { name: 'Doe, Marcus L.',     class: 'Lineman',                 rate: 47.85, fringe: 8.20, hours: [10, 10, 10, 10, 8, 0, 0] },
  { name: 'Hernandez, Carlos',  class: 'Lineman',                 rate: 47.85, fringe: 8.20, hours: [10, 10, 10, 10, 8, 0, 0] },
  { name: 'Park, Daniel J.',    class: 'Splicer',                 rate: 41.20, fringe: 7.45, hours: [10, 10, 10, 10, 8, 0, 0] },
  { name: 'Williams, Trey A.',  class: 'Equipment Operator',      rate: 38.95, fringe: 6.80, hours: [10, 10, 10, 10, 8, 0, 0] },
  { name: 'Otieno, Brian',      class: 'Laborer',                 rate: 27.50, fringe: 5.90, hours: [10, 10, 10, 10, 8, 4, 0] },
];

const fmtUSD = (n: number) => `$${n.toFixed(2)}`;
const fmtUSDInt = (n: number) => `$${Math.round(n).toLocaleString()}`;

function parseLocalDate(input: any): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

const fmtDate = (d: any) => {
  const dt = parseLocalDate(d);
  return dt ? dt.toLocaleDateString() : '—';
};

export default function WH347Page() {
  const { obligationId } = useParams();

  const { data: project } = useQuery<any>({
    queryKey: ['bead-project-wh347', DEMO_PROJECT_ID],
    queryFn: async () => {
      const all = await api.get('/bead/projects');
      return Array.isArray(all) ? all.find((p: any) => p.id === DEMO_PROJECT_ID) : null;
    },
  });

  const { data: obligations = [] } = useQuery<ObligationDetail[]>({
    queryKey: ['wh347-obligations', DEMO_PROJECT_ID],
    queryFn: () => api.get(`/bead-pm/projects/${DEMO_PROJECT_ID}/obligations`),
  });

  const obligation = obligations.find(o => o.id === obligationId);

  // Compute payroll number — count of WH-347 obligations with earlier due_date + 1.
  const wh347s = obligations
    .filter(o => o.code === 'FED-LAB-WH347')
    .sort((a, b) => (a.due_date ?? '').localeCompare(b.due_date ?? ''));
  const payrollNum = obligation ? wh347s.findIndex(o => o.id === obligation.id) + 1 : 1;

  if (!obligation) {
    return <div style={{ padding: 40 }}>Loading WH-347…</div>;
  }

  // Per-worker totals. Note: don't shadow `fringe` (the per-hour fringe rate
  // from ROSTER) with `fringeEarned` (the weekly total). Got bitten by that.
  const rows = ROSTER.map(w => {
    const totalHrs = w.hours.reduce((s, h) => s + h, 0);
    const otHrs = Math.max(0, totalHrs - 40);
    const stHrs = Math.min(40, totalHrs);
    const grossST = stHrs * w.rate;
    const grossOT = otHrs * w.rate * 1.5;
    const fringeEarned = totalHrs * w.fringe;
    const gross = grossST + grossOT + fringeEarned;
    const fica   = gross * 0.0765;
    const fedTax = gross * 0.10;
    const stateTax = gross * 0.045;
    const totalDed = fica + fedTax + stateTax;
    const net = gross - totalDed;
    return { ...w, totalHrs, stHrs, otHrs, grossST, grossOT, fringeEarned, gross, fica, fedTax, stateTax, totalDed, net };
  });

  const grandHours = rows.reduce((s, r) => s + r.totalHrs, 0);
  const grandGross = rows.reduce((s, r) => s + r.gross, 0);
  const grandNet   = rows.reduce((s, r) => s + r.net, 0);

  return (
    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '40px 50px',
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 11, color: '#0f172a', lineHeight: 1.4, background: 'white',
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          @page { size: letter landscape; margin: 0.4in; }
        }
        table.payroll { border-collapse: collapse; width: 100%; font-size: 9px; }
        table.payroll th, table.payroll td { border: 1px solid #475569; padding: 3px 4px; text-align: left; vertical-align: top; }
        table.payroll thead th { background: #e2e8f0; font-weight: 700; font-size: 8px; text-transform: uppercase; letter-spacing: .03em; text-align: center; }
        .day-cell { width: 22px; text-align: center !important; font-variant-numeric: tabular-nums; }
        .num-cell { text-align: right !important; font-variant-numeric: tabular-nums; white-space: nowrap; }
      `}</style>

      <div className="no-print" style={{ marginBottom: 20, padding: 10, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, textAlign: 'center', fontFamily: 'system-ui' }}>
        Use your browser's <b>Print → Save as PDF</b> (landscape) to export this WH-347.
        <button onClick={() => window.print()}
          style={{ marginLeft: 12, padding: '4px 12px', background: '#0f172a', color: '#fff', border: 0, borderRadius: 3, cursor: 'pointer' }}>Print</button>
      </div>

      {/* Form header — official-style */}
      <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10, color: '#475569' }}>U.S. Department of Labor · Wage and Hour Division</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>PAYROLL · Form WH-347</div>
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>(For Contractor's Optional Use; See Instructions, Form WH-347 Inst.)</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9 }}>OMB No. 1235-0008</div>
            <div style={{ fontSize: 9 }}>Expires: 09/30/2026</div>
          </div>
        </div>
      </div>

      {/* Project / Contractor metadata */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14, fontSize: 10 }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(1) NAME OF CONTRACTOR / SUBCONTRACTOR</div>
              <div style={{ fontWeight: 700 }}>Vistabeam Wireless Internet Services, LLC</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(2) ADDRESS</div>
              <div>112 W 11th St., Sidney, NE 69162</div>
            </td>
          </tr>
          <tr>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(3) PAYROLL NO.</div>
              <div style={{ fontWeight: 700 }}>{String(payrollNum).padStart(3, '0')}</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(4) FOR WEEK ENDING</div>
              <div style={{ fontWeight: 700 }}>{fmtDate(obligation.period_end ?? obligation.due_date)}</div>
            </td>
          </tr>
          <tr>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(5) PROJECT AND LOCATION</div>
              <div>{project?.project_name ?? 'Larimer01'} · {project?.county ?? 'Larimer'} County, {project?.state ?? 'CO'}</div>
            </td>
            <td style={{ padding: '4px 6px', border: '1px solid #475569' }}>
              <div style={{ fontSize: 8, color: '#64748b' }}>(6) PROJECT OR CONTRACT NO.</div>
              <div>{project?.project_code ?? 'App-000711'} · CO BEAD Subgrant 2024</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Worker rows */}
      <table className="payroll">
        <thead>
          <tr>
            <th rowSpan={2} style={{ width: '14%' }}>(1) Name & Individual ID</th>
            <th rowSpan={2} style={{ width: 30 }}>(2) No. of W/H Exemptions</th>
            <th rowSpan={2} style={{ width: '11%' }}>(3) Work Classification</th>
            <th colSpan={9}>(4) Day & Date</th>
            <th rowSpan={2}>(5) Total Hours</th>
            <th rowSpan={2}>(6) Rate of Pay (incl. fringe)</th>
            <th colSpan={2}>(7) Gross Amount Earned</th>
            <th colSpan={4}>(8) Deductions</th>
            <th rowSpan={2}>(9) Net Wages Paid</th>
          </tr>
          <tr>
            <th className="day-cell">M</th>
            <th className="day-cell">T</th>
            <th className="day-cell">W</th>
            <th className="day-cell">T</th>
            <th className="day-cell">F</th>
            <th className="day-cell">S</th>
            <th className="day-cell">S</th>
            <th>O/T</th>
            <th>S/T</th>
            <th>This Project</th>
            <th>All Projects</th>
            <th>FICA</th>
            <th>Fed</th>
            <th>State</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.name}</td>
              <td className="day-cell">2</td>
              <td>{r.class}</td>
              {r.hours.map((h, j) => (
                <td key={j} className="day-cell">{h || ''}</td>
              ))}
              <td className="num-cell">{r.otHrs || '—'}</td>
              <td className="num-cell">{r.stHrs}</td>
              <td className="num-cell">{r.totalHrs}</td>
              <td className="num-cell">{fmtUSD(r.rate + r.fringe)}/hr</td>
              <td className="num-cell">{fmtUSD(r.gross)}</td>
              <td className="num-cell">{fmtUSD(r.gross)}</td>
              <td className="num-cell">{fmtUSD(r.fica)}</td>
              <td className="num-cell">{fmtUSD(r.fedTax)}</td>
              <td className="num-cell">{fmtUSD(r.stateTax)}</td>
              <td className="num-cell">{fmtUSD(r.totalDed)}</td>
              <td className="num-cell" style={{ fontWeight: 700 }}>{fmtUSD(r.net)}</td>
            </tr>
          ))}
          <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
            <td colSpan={3}>WEEKLY TOTAL</td>
            <td className="day-cell" colSpan={9}>—</td>
            <td className="num-cell">{grandHours}</td>
            <td>—</td>
            <td className="num-cell">{fmtUSDInt(grandGross)}</td>
            <td className="num-cell">{fmtUSDInt(grandGross)}</td>
            <td colSpan={4} className="num-cell">{fmtUSDInt(grandGross - grandNet)}</td>
            <td className="num-cell">{fmtUSDInt(grandNet)}</td>
          </tr>
        </tbody>
      </table>

      {/* Statement of Compliance */}
      <div style={{ marginTop: 20, padding: 14, border: '2px solid #0f172a', background: '#f8fafc' }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>STATEMENT OF COMPLIANCE</div>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          Date <b>{new Date().toLocaleDateString()}</b>. I, <b>Kevin M. Mullin, President</b>, do hereby state:
          (1) That I pay or supervise the payment of the persons employed by Vistabeam Wireless Internet Services, LLC
          on the {project?.project_name ?? 'Larimer01'} BEAD project; that during the payroll period commencing on the
          {' '}<b>{fmtDate(obligation.period_start)}</b> day, and ending the <b>{fmtDate(obligation.period_end)}</b> day,
          all persons employed on said project have been paid the full weekly wages earned, that no rebates have been
          or will be made either directly or indirectly to or on behalf of said contractor from the full weekly wages
          earned by any person and that no deductions have been made either directly or indirectly from the full wages
          earned by any person, other than permissible deductions as defined in Regulations, Part 3 (29 C.F.R. Subtitle A).
        </p>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          (2) That any payrolls otherwise under this contract required to be submitted for the above period are correct
          and complete; that the wage rates for laborers or mechanics contained therein are not less than the applicable
          wage rates contained in any wage determination incorporated into the contract; that the classifications set
          forth therein for each laborer or mechanic conform with the work performed.
        </p>
        <p style={{ fontSize: 10, lineHeight: 1.5 }}>
          (3) That any apprentices employed in the above period are duly registered in a bona fide apprenticeship
          program registered with a State apprenticeship agency recognized by the Bureau of Apprenticeship and
          Training, U.S. Department of Labor.
        </p>
        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 28 }} />
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Signature of Contractor or Subcontractor</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #0f172a', height: 28 }} />
            <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Title</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 8, color: '#94a3b8', textAlign: 'center', fontFamily: 'system-ui' }}>
        Generated by Milestone Build · {new Date().toLocaleString()} · Wage Determination CO20240003 ·
        Auto-populated from payroll feed · For SBO submission upon signature
      </div>
    </div>
  );
}
